<template>
  <main class="min-h-screen bg-gray-300">
    <header>
      <div
        class="bg-white border-b border-gray-200 px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8"
      >
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-medium leading-6 text-gray-900 sm:truncate">
            {{ $t('invoice_email_title') }}
          </h1>
        </div>
        <div class="mt-4 flex sm:mt-0 sm:ml-4 space-x-2"></div>
      </div>
      <sqr-progress v-show="loading" />
      <sqr-error-banner :error="loadError" />
    </header>

    <section class="py-10">
      <div class="max-w-sm mx-auto">
        <div class="bg-white p-4 rounded shadow">
          <div class="">
            <sqr-input-email
              class="mb-3"
              :label="$t('invoice_email_to')"
              v-model="to"
            />
            <sqr-input-checkbox
              class="mb-3"
              v-show="cc"
              :placeholder="$t('invoice_email_cc_practice', { email: cc })"
              v-model="ccPractice"
            />
            <sqr-error-text class="field" :error="sendError" />
            <div class="flex space-x-2 justify-end">
              <sqr-button
                :label="$t('buttons_cancel')"
                @click="$router.back()"
                color="white"
              />
              <sqr-button
                :label="$t('buttons_send')"
                @click="send"
                color="green"
                :is-loading="sending"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import SqrProgress from '@/components/SqrProgress';
import SqrErrorBanner from '@/components/SqrErrorBanner';
import SqrInputEmail from '@/components/SqrInputEmail';
import SqrInputCheckbox from '@/components/SqrInputCheckbox';
import SqrErrorText from '@/components/SqrErrorText';
import SqrButton from '@/components/SqrButton';

import invoiceMake from '@/utils/invoiceMake';

export default {
  name: 'InvoiceEmail',
  components: {
    SqrProgress,
    SqrErrorBanner,
    SqrErrorText,
    SqrInputEmail,
    SqrInputCheckbox,
    SqrButton,
  },
  props: {
    id: String,
  },
  computed: {
    ...mapGetters('auth', ['uid', 'userEmail', 'displayName']),
    ...mapGetters('account', ['accountId', 'account']),
    ...mapGetters('invoice', ['invoiceId', 'invoice', 'loading', 'loadError']),
  },
  data() {
    return {
      to: '',
      cc: '',
      ccPractice: false,
      sending: false,
      sendError: null,
    };
  },
  async mounted() {
    this.$a().logEvent('invoice_email');
    const accountId = this.accountId;
    const id = this.id;
    const invoice = await this.subscribe({ accountId, id });
    this.to = (invoice && invoice.patient && invoice.patient.email) || '';
    this.cc = (invoice && invoice.issuer && invoice.issuer.email) || '';
    const patientId = invoice && invoice.patientId;
    if (!this.to && patientId) {
      const snap = await this.$db()
        .doc(`accounts/${accountId}/patients/${patientId}`)
        .get();
      if (snap.exists) {
        this.to = snap.data().email || '';
      }
    }
  },
  methods: {
    ...mapActions('invoice', ['subscribe']),
    async send() {
      try {
        this.sending = true;
        this.sendError = null;
        const cc = this.ccPractice ? [this.cc] : [];

        const invoice = {
          ...this.invoice,
          copies: this.invoice.copies.filter(v => v !== 'issuer'),
        };

        const pdf = await invoiceMake(
          invoice,
          this,
          this.$i18n && this.$i18n.current,
          this.uid
        );
        const dataUrl = await new Promise(resolve => {
          pdf.getDataUrl(resolve);
        });

        this.$db()
          .collection('emails')
          .add({
            uid: this.uid,
            to: this.to,
            cc,
            replyTo: this.userEmail,
            // headers: { 'Return-Path': this.userEmail },
            // Return-Path is checked as well by SPF.
            // We should handle the bounces using an email address from the same domain
            // I don't want to invest time in an extra feature.
            template: {
              name: 'invoice-fr',
              data: {
                displayName: this.displayName,
                companyName:
                  (this.invoice &&
                    this.invoice.issuer &&
                    this.invoice.issuer.company) ||
                  '',
                invoiceNumber: (this.invoice && this.invoice.number) || '',
              },
            },
            message: {
              attachments: [
                {
                  filename: this.invoice.number + '.pdf',
                  href: dataUrl,
                  // encoding: 'base64'
                },
              ],
            },
          });
        this.$router.back();
      } catch (error) {
        this.sendError = error;
        return Promise.reject(error);
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
